import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import "./multiRangeSlider.css";
import { useLocation } from "react-router-dom";

const MultiRangeSlider = ({ min, max, handlePriceChange, currencySymbol, reset }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const searchPrice = params.get("priceRange");

  const [minVal, setMinVal] = useState(searchPrice ? +searchPrice.split("-")[0] : min);
  const [maxVal, setMaxVal] = useState(searchPrice ? +searchPrice.split("-")[1] : max);
  const minValRef = useRef(minVal);
  const maxValRef = useRef(maxVal);
  const range = useRef(null);

  // Convert to percentage
  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  const handleOnChange = (newMin, newMax) => {
    setMinVal(newMin);
    setMaxVal(newMax);
    minValRef.current = newMin;
    maxValRef.current = newMax;
    handlePriceChange(newMin, newMax);
  };

  // Set width of the range
  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, getPercent]);

  useEffect(() => {
    const minPercent = getPercent(minValRef.current);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, getPercent]);

  // **Handle Reset: When reset prop changes, reset values**
  useEffect(() => {
    if (reset) {
      setMinVal(min);
      setMaxVal(max);
      minValRef.current = min;
      maxValRef.current = max;

      if (range.current) {
        range.current.style.left = "0%";
        range.current.style.width = "100%";
      }
      handlePriceChange(min, max);
    }
  }, [reset, min, max, handlePriceChange]);

  return (
    <div className="range-container">
      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        onChange={(event) => {
          const value = Math.min(Number(event.target.value), maxVal - 1);
          handleOnChange(value, maxVal);
        }}
        className="thumb thumb--left"
        style={{ zIndex: minVal > max - 100 && "5" }}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        onChange={(event) => {
          const value = Math.max(Number(event.target.value), minVal + 1);
          handleOnChange(minVal, value);
        }}
        className="thumb thumb--right"
      />

      <div className="slider">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
        <div className="slider__left-value">
          <span className="slider-left-wrap">
            {currencySymbol}
            <input value={minVal} readOnly />
          </span>
        </div>
        <div className="slider__right-value">
          <span className="slider-right-wrap">
            {currencySymbol}
            <input value={maxVal} readOnly />
          </span>
        </div>
      </div>
    </div>
  );
};

MultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  handlePriceChange: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  reset: PropTypes.bool, // New prop to trigger reset
};

export default MultiRangeSlider;
